import React, { useState, useEffect } from "react";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import Service from "../webservice/http";

// Role levels — using exact strings stored in localStorage/sessionStorage
// Lower number = higher in hierarchy
const ROLE_LEVEL = {
  Management: 0, // sees: LOB Head, Manager (2 levels below)
  LOB: 1, // sees: Manager, Team Lead (2 levels below)
  Manager: 2, // sees: Team Lead, Agent (2 levels below)
  Managerh: 2, // sees: Team Lead, Agent (2 levels below)
  "Master Collections": 2, // sees: Team Lead, Agent (2 levels below)
  "Team Lead": 3, // sees: Agent only (1 level below)
  Agent: 4, // no filter shown
  "Agent Collection": 4, // no filter shown
};

const AGENT_ROLES = ["Agent Collections", "Agenth", "Agent", "DCA Agent"];

// Hierarchy dropdown fields.
// A field is VISIBLE when: loggedLevel <= f.minLevel
//   Management(0) → sees lob(≤0), manager(≤1), team_lead(≤2), agent(≤3) → all 4
//   LOB       (1) → skips lob,   sees manager(≤1), team_lead(≤2), agent(≤3) → 3
//   Managerh  (2) → skips lob,manager, sees team_lead(≤2), agent(≤3) → 2
//   Team Lead (3) → skips all except agent(≤3) → 1
//   Agent     (4) → 4 <= anything is false → 0 fields
const HIERARCHY_FIELDS = [
  { key: "lob", label: "LOB Head / VP Collections", minLevel: 0 },
  { key: "manager", label: "Manager", minLevel: 1 },
  { key: "team_lead", label: "Team Lead", minLevel: 2 },
  { key: "agent", label: "Agent", minLevel: 3 },
];

const CARD_STYLE = {
  backgroundColor: "#fff",
  borderRadius: "6px",
  padding: "12px 14px",
  marginBottom: "10px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
};

const CARD_HEADING_STYLE = {
  fontWeight: "700",
  color: "#271078",
  borderBottom: "2px solid #271078",
  paddingBottom: "6px",
  marginBottom: "10px",
  marginTop: "0",
};

const SELECT_STYLE = {
  width: "100%",
  padding: "6px 10px",
  border: "1px solid #ccc",
  borderRadius: "4px",
  backgroundColor: "#fff",
  color: "#333",
  marginBottom: "10px",
};

const ReportFilter = ({
  width,
  changeSideBarWidth,
  onApplyFilter,
  onResetFilter,
  userRole = "",
}) => {
  const isAgentRole = AGENT_ROLES.includes((userRole || "").trim());
  const loggedLevel = ROLE_LEVEL[userRole] ?? 99;

  // Show fields that are below or at the logged-in role level
  const visibleHierarchyFields = HIERARCHY_FIELDS.filter(
    (f) => loggedLevel <= f.minLevel,
  );

  const [filterData, setFilterData] = useState({
    lobs: [],
    managers: [],
    teamLeads: [],
    agents: [],
    dcaOrgs: [],
    dcaManagers: [],
    dcaTeams: [],
    dcaAgents: [],
    timePeriods: [],
    campaigns: [],
    strategies: [],
    productTypes: [],
    bucketTypes: [],
  });

  const [selectionRange, setSelectionRange] = useState({
    startDate: new Date(),
    endDate: new Date(),
    key: "selection",
  });

  const EMPTY_FILTERS = {
    lob: "",
    manager: "",
    team_lead: "",
    agent: "",
    dca_organization: "",
    dca_manager: "",
    dca_team: "",
    dca_agent: "",
    time_period: "",
    start_date: null,
    end_date: null,
    campaign_name: "",
    collection_strategy: "",
    product_type: "",
    bucket_type: "",
  };

  const [selectedFilters, setSelectedFilters] = useState({ ...EMPTY_FILTERS });

  useEffect(() => {
    const services = new Service();
    const queryParams = new URLSearchParams();

    if (selectedFilters.lob)
      queryParams.append("lob_name", selectedFilters.lob);
    if (selectedFilters.manager)
      queryParams.append("manager_name", selectedFilters.manager);
    if (selectedFilters.team_lead)
      queryParams.append("team_lead_name", selectedFilters.team_lead);
    if (selectedFilters.agent)
      queryParams.append("agent_name", selectedFilters.agent);

    if (selectedFilters.dca_organization)
      queryParams.append(
        "dca_organization_name",
        selectedFilters.dca_organization,
      );
    if (selectedFilters.dca_manager)
      queryParams.append("dca_manager_name", selectedFilters.dca_manager);
    if (selectedFilters.dca_team)
      queryParams.append("dca_team_lead_name", selectedFilters.dca_team);
    if (selectedFilters.dca_agent)
      queryParams.append("dca_agent_name", selectedFilters.dca_agent);

    const qs = queryParams.toString();
    const endpoint = `api/call_verification/hierarchy-filters/${
      qs ? `?${qs}` : ""
    }`;

    services
      .get(endpoint)
      .then((res) => {
        if (!res || !res.filters) return;

        const f = res.filters;

        setFilterData((prev) => ({
          lobs:
            selectedFilters.lob && prev.lobs.length > 0
              ? prev.lobs
              : (f.organization_filters?.lob || []).map((i) => i.name),

          managers:
            selectedFilters.manager && prev.managers.length > 0
              ? prev.managers
              : (f.organization_filters?.manager_collections || []).map(
                  (i) => i.name,
                ),

          teamLeads:
            selectedFilters.team_lead && prev.teamLeads.length > 0
              ? prev.teamLeads
              : (f.organization_filters?.team_lead || []).map((i) => i.name),

          agents:
            selectedFilters.agent && prev.agents.length > 0
              ? prev.agents
              : (f.organization_filters?.agent || []).map((i) => i.name),

          dcaOrgs:
            selectedFilters.dca_organization && prev.dcaOrgs.length > 0
              ? prev.dcaOrgs
              : (f.dca_filters?.dca_organization || []).map((i) => i.name),

          dcaManagers:
            selectedFilters.dca_manager && prev.dcaManagers.length > 0
              ? prev.dcaManagers
              : (f.dca_filters?.dca_manager || []).map((i) => i.name),

          dcaTeams:
            selectedFilters.dca_team && prev.dcaTeams.length > 0
              ? prev.dcaTeams
              : (f.dca_filters?.dca_team_lead || []).map((i) => i.name),

          dcaAgents:
            selectedFilters.dca_agent && prev.dcaAgents.length > 0
              ? prev.dcaAgents
              : (f.dca_filters?.dca_agent || []).map((i) => i.name),

          timePeriods: f.time_filters?.period_options || [],
          campaigns:
            selectedFilters.campaign_name && prev.campaigns.length > 0
              ? prev.campaigns
              : (f.campaign_filters?.campaign_name || []).map((i) => i.name),
          strategies:
            selectedFilters.collection_strategy && prev.strategies.length > 0
              ? prev.strategies
              : (f.campaign_filters?.collection_strategy || []).map(
                  (i) => i.label,
                ),
          productTypes:
            selectedFilters.product_type && prev.productTypes.length > 0
              ? prev.productTypes
              : (f.campaign_filters?.product_type || []).map((i) => i.title),
          bucketTypes: f.bucket_type_filters?.bucket_types || [],
        }));
      })
      .catch(console.error);
  }, []);

  const handleChange = (key, value) =>
    setSelectedFilters((prev) => ({ ...prev, [key]: value }));

  const formatDate = (date) => {
    if (!date) return "";
    return `${String(date.getDate()).padStart(2, "0")}/${String(
      date.getMonth() + 1,
    ).padStart(2, "0")}/${date.getFullYear()}`;
  };

  const handleDateRangeSelect = (ranges) => {
    setSelectionRange(ranges.selection);
    setSelectedFilters((prev) => ({
      ...prev,
      start_date: ranges.selection.startDate,
      end_date: ranges.selection.endDate,
      time_period: "",
    }));
  };

  const handleApply = () => {
    const f = {};
    const plain = [
      "lob",
      "manager",
      "team_lead",
      "agent",
      "dca_organization",
      "dca_manager",
      "dca_team",
      "dca_agent",
      "time_period",
      "campaign_name",
      "collection_strategy",
      "product_type",
      "bucket_type",
    ];
    plain.forEach((k) => {
      if (selectedFilters[k]) f[k] = selectedFilters[k];
    });
    if (selectedFilters.start_date)
      f.from_date = formatDate(selectedFilters.start_date);
    if (selectedFilters.end_date)
      f.to_date = formatDate(selectedFilters.end_date);
    changeSideBarWidth();
    if (onApplyFilter) onApplyFilter(f);
  };

  const handleReset = () => {
    setSelectedFilters({ ...EMPTY_FILTERS });
    setSelectionRange({
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    });
    changeSideBarWidth();
    if (onResetFilter) onResetFilter();
  };

  const FIELD_OPTIONS = {
    lob: filterData.lobs,
    manager: filterData.managers,
    team_lead: filterData.teamLeads,
    agent: filterData.agents,
  };

  const SelectField = ({ label, filterKey, options }) => (
    <div className="" style={{ paddingBottom: "1px" }}>
      <div className="my-calls-column">
        <div className="row">
          <div className="col-md-6">
            <div className="form-col clearfix">
              <p className="blue">{label}</p>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-12 px-8">
            <select
              value={selectedFilters[filterKey]}
              onChange={(e) => handleChange(filterKey, e.target.value)}
              style={SELECT_STYLE}
            >
              <option value="">-- All --</option>
              {options.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div
        className="filterDiv overflow-y-auto"
        style={{
          height: "100%",
          width: width,
          position: "fixed",
          zIndex: "2000",
          top: "0",
          right: "0",
          backgroundColor: "#DEE3E8",
          transition: "0.5s",
          overflowX: "hidden",
          paddingBottom: "60px",
        }}
      >
        <div style={{ padding: "10px 10px 0" }}>
          {visibleHierarchyFields.length > 0 && (
            <div style={CARD_STYLE}>
              <p style={CARD_HEADING_STYLE}>Organization Filters</p>
              {visibleHierarchyFields.map(({ key, label }) => (
                <SelectField
                  key={key}
                  label={label}
                  filterKey={key}
                  options={FIELD_OPTIONS[key]}
                />
              ))}
            </div>
          )}

          {!isAgentRole && (
            <div style={CARD_STYLE}>
              <p style={CARD_HEADING_STYLE}>DCA Filters</p>
              <SelectField
                label="DCA Organization"
                filterKey="dca_organization"
                options={filterData.dcaOrgs}
              />
              <SelectField
                label="DCA Manager"
                filterKey="dca_manager"
                options={filterData.dcaManagers}
              />
              <SelectField
                label="DCA Team Lead"
                filterKey="dca_team"
                options={filterData.dcaTeams}
              />
              <SelectField
                label="DCA Agent"
                filterKey="dca_agent"
                options={filterData.dcaAgents}
              />
            </div>
          )}

          <div style={CARD_STYLE}>
            <p style={CARD_HEADING_STYLE}>Time Filters</p>

            <div className="" style={{ paddingBottom: "1px" }}>
              <div className="my-calls-column">
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-col clearfix">
                      <p className="blue">Period</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
                marginBottom: "14px",
              }}
            >
              {filterData.timePeriods.map((p) => {
                const isActive =
                  selectedFilters.time_period === p.toLowerCase();
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() =>
                      handleChange(
                        "time_period",
                        isActive ? "" : p.toLowerCase(),
                      )
                    }
                    style={{
                      padding: "5px 16px",
                      borderRadius: "20px",
                      border: "1px solid #271078",
                      backgroundColor: isActive ? "#271078" : "#fff",
                      color: isActive ? "#fff" : "#271078",
                      fontSize: "12px",
                      fontWeight: "600",
                      cursor: "pointer",
                    }}
                  >
                    {p}
                  </button>
                );
              })}
            </div>

            <div className="" style={{ paddingBottom: "4px" }}>
              <div className="my-calls-column">
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-col clearfix">
                      <p className="blue">Custom Date Range</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div style={{ overflowX: "auto" }}>
              <DateRangePicker
                ranges={[selectionRange]}
                onChange={handleDateRangeSelect}
                showMonthAndYearPickers={false}
              />
            </div>
          </div>

          <div style={CARD_STYLE}>
            <p style={CARD_HEADING_STYLE}>Campaign Filters</p>
            <SelectField
              label="Campaign Name"
              filterKey="campaign_name"
              options={filterData.campaigns}
            />
            <SelectField
              label="Collection Status"
              filterKey="collection_strategy"
              options={filterData.strategies}
            />
            <SelectField
              label="Product Type"
              filterKey="product_type"
              options={filterData.productTypes}
            />

            <div className="mb-20" style={{ paddingBottom: "1px" }}>
              <div className="my-calls-column">
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-col clearfix">
                      <p className="blue">Bucket Type</p>
                    </div>
                  </div>
                </div>
                <div className="row">
                  {filterData.bucketTypes.map(({ label, value }) => (
                    <div className="col-md-6" key={value}>
                      <div className="select-check">
                        <label
                          className="checkbox-container"
                          style={{ fontSize: "15px" }}
                        >
                          {label}
                          <input
                            type="radio"
                            name="bucket_type"
                            value={value}
                            checked={selectedFilters.bucket_type === value}
                            onChange={() => handleChange("bucket_type", value)}
                          />
                          <span className="checkmark"></span>
                        </label>
                      </div>
                    </div>
                  ))}
                  {selectedFilters.bucket_type && (
                    <div
                      className="col-md-12 px-8"
                      style={{ marginTop: "4px" }}
                    >
                      <button
                        type="button"
                        onClick={() => handleChange("bucket_type", "")}
                        style={{
                          background: "none",
                          border: "none",
                          color: "rgb(249, 158, 82)",
                          fontSize: "12px",
                          cursor: "pointer",
                          padding: "0",
                          fontWeight: "600",
                        }}
                      >
                        ✕ Clear selection
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className="w-full fixed right-0 bottom-0 bg-white py-2"
        style={{
          zIndex: "5000",
          width: width,
          height: "46px",
          transition: "0.5s",
        }}
      >
        <div className="px-8">
          <button
            className="btn Save"
            style={{ backgroundColor: "rgb(249, 158, 82)", color: "white" }}
            type="button"
            onClick={handleReset}
          >
            Reset
          </button>
          <button
            className="btn Save ml-4"
            style={{ backgroundColor: "#271078", color: "white" }}
            type="button"
            onClick={handleApply}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportFilter;
